import './styles/main.css';
import './config/i18n';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, StrictMode, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AdminLayout } from './components/AdminLayout';
import { LegacyRedirect } from './components/organisms/LegacyRedirect';
import { ProtectedRoute } from './components/ProtectedRoute';
import queryClient from './config/queryClient';
import { LEGACY_PARAM_REDIRECTS, LEGACY_REDIRECTS, ROUTES } from './config/routes';
import ModuleFilterProvider from './context/ModuleFilter';
import PermissionsProvider from './context/Permissions';
import { ConversationDetail } from './pages/ConversationDetail';
import { Conversations } from './pages/Conversations';
import { Costs } from './pages/Costs';
import { Dashboard } from './pages/Dashboard';
import { Dependencies } from './pages/Dependencies';
import { Evaluations } from './pages/Evaluations';
import { Glossary } from './pages/Glossary';
import { JudgeDetail } from './pages/JudgeDetail';
import { Judges } from './pages/Judges';
import { ModuleDetail } from './pages/ModuleDetail';
import { Modules } from './pages/Modules';
import { Quality } from './pages/Quality';
import { Tools } from './pages/Tools';

// The whole section is lazy: it is the only one that writes, most people never
// open it, and its editor pulls the preview panel and the revision history with
// it.
const Knowledge = lazy(() => import('./pages/Knowledge'));
const KnowledgeArticles = lazy(() => import('./pages/Knowledge/KnowledgeArticles'));
const KnowledgeArticleDetail = lazy(() => import('./pages/Knowledge/KnowledgeArticleDetail'));
const KnowledgeChunks = lazy(() => import('./pages/Knowledge/KnowledgeChunks'));
const KnowledgeVersions = lazy(() => import('./pages/Knowledge/KnowledgeVersions'));
const KnowledgeAudit = lazy(() => import('./pages/Knowledge/KnowledgeAudit'));

// Its own editor + JSON snapshot tooling pulls extra weight most people never
// exercise — same reasoning as the Knowledge section above.
const Playground = lazy(() => import('./pages/Playground'));

const Routing = lazy(() => import('./pages/Routing').then((m) => ({ default: m.Routing })));

const LazyPageFallback = () => <div className="min-h-screen animate-pulse bg-grey-25" />;

const App = () => (
	<StrictMode>
		<div className="mf-ai-agents">
			<QueryClientProvider client={queryClient}>
				<PermissionsProvider>
					<ModuleFilterProvider>
						<BrowserRouter>
							<Routes>
								<Route element={<AdminLayout />}>
									<Route
										index
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Dashboard />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.conversations}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Conversations />
											</ProtectedRoute>
										}
									/>
									<Route
										path={`${ROUTES.conversations}/:threadId`}
										element={
											<ProtectedRoute permission={(p) => !!p.show}>
												<ConversationDetail />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.quality}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Quality />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.routing}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Suspense fallback={<LazyPageFallback />}>
													<Routing />
												</Suspense>
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.tools}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Tools />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.costs}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Costs />
											</ProtectedRoute>
										}
									/>
									{/* Reading only takes `show`; update/delete are gated inside by useKnowledgeCapabilities. */}
									<Route
										path={ROUTES.knowledge}
										element={
											<ProtectedRoute permission={(p) => !!p.show}>
												<Suspense fallback={<LazyPageFallback />}>
													<Knowledge />
												</Suspense>
											</ProtectedRoute>
										}
									>
										<Route index element={<KnowledgeArticles />} />
										<Route path="articles/:sourceId" element={<KnowledgeArticleDetail />} />
										<Route path="chunks" element={<KnowledgeChunks />} />
										<Route path="versions" element={<KnowledgeVersions />} />
										<Route path="audit" element={<KnowledgeAudit />} />
									</Route>
									<Route
										path={ROUTES.lab}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Suspense fallback={<LazyPageFallback />}>
													<Playground />
												</Suspense>
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.labEvaluations}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Evaluations />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.labJudges}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Judges />
											</ProtectedRoute>
										}
									/>
									<Route
										path={`${ROUTES.labJudges}/:id`}
										element={
											<ProtectedRoute permission={(p) => !!p.show}>
												<JudgeDetail />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.health}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Dependencies />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.healthModules}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Modules />
											</ProtectedRoute>
										}
									/>
									<Route
										path={`${ROUTES.healthModules}/:moduleName`}
										element={
											<ProtectedRoute permission={(p) => !!p.show}>
												<ModuleDetail />
											</ProtectedRoute>
										}
									/>
									<Route
										path={ROUTES.glossary}
										element={
											<ProtectedRoute permission={(p) => !!p.list}>
												<Glossary />
											</ProtectedRoute>
										}
									/>
									{LEGACY_REDIRECTS.map(([from, to]) => (
										<Route key={from} path={from} element={<Navigate to={to} replace />} />
									))}
									{LEGACY_PARAM_REDIRECTS.map(({ from }) => (
										<Route key={from} path={from} element={<LegacyRedirect from={from} />} />
									))}
									<Route path="*" element={<Navigate to={ROUTES.today} replace />} />
								</Route>
							</Routes>
						</BrowserRouter>
					</ModuleFilterProvider>
				</PermissionsProvider>
				<ReactQueryDevtools />
			</QueryClientProvider>
		</div>
	</StrictMode>
);

export default App;
